import React from "react";
import { Head, Link } from "@inertiajs/react";
import { Button, Grid, Group, Stack } from "@mantine/core";
import { IconArrowLeft } from "@tabler/icons-react";
import PageShell from "../Components/Layout/PageShell";
import { Heading1, DimmedText } from "../Components/UI/Typography";
import { Badge } from "../Components/UI/Badge";
import { PostCard } from "../Components/PostCard";
import type { FormatType, PillarSlug } from "../data/posts";
import { getPostsByPillarAndFormat } from "../data/posts";

interface PostFormatIndexProps {
  pillar: PillarSlug;
  format: FormatType;
  /** Optional overrides — normally derived from INDEX_COPY below */
  title?: string;
  intro?: string;
  emptyMessage?: string;
}

const PILLAR_LABELS: Record<PillarSlug, string> = {
  tech: "Tech",
  dance: "Dance",
  media: "Media",
  think: "Think",
};

// Per (pillar, format) hero copy for the 12 index pages.
// Keyed as `${pillar}-${format}` for flat lookup.
const INDEX_COPY: Record<string, { title: string; intro: string }> = {
  "tech-article": {
    title: "Tech Articles",
    intro:
      "Deep dives on architecture, systems, and lessons from shipping.",
  },
  "tech-video": {
    title: "Tech Videos",
    intro: "Demos, walkthroughs, and talks about the tech stack.",
  },
  "tech-podcast": {
    title: "Tech Podcasts",
    intro: "Conversations about building software and the tech industry.",
  },
  "dance-article": {
    title: "Dance Articles",
    intro:
      "Writing on Urban Kiz technique, teaching, and the culture around the scene.",
  },
  "dance-video": {
    title: "Dance Videos",
    intro: "Kiz drills, breakdowns, and moments from the floor.",
  },
  "dance-podcast": {
    title: "Dance Podcasts",
    intro:
      "Conversations with dancers, teachers, and organizers in the Kizomba world.",
  },
  "media-article": {
    title: "Media Articles",
    intro:
      "Behind-the-scenes notes on shoots, gear choices, and the craft.",
  },
  "media-video": {
    title: "Media Videos",
    intro: "Event edits, reels, and short-form video work.",
  },
  "media-podcast": {
    title: "Media Podcasts",
    intro: "Conversations with photographers, videographers, and creatives.",
  },
  "think-article": {
    title: "Think — Essays",
    intro:
      "Long-form writing on culture, systems, and self.",
  },
  "think-video": {
    title: "Think — Videos",
    intro: "Video essays and talks on the ideas connecting the work.",
  },
  "think-podcast": {
    title: "Think — Podcasts",
    intro:
      "Long-form conversations on the questions I keep coming back to.",
  },
};

/**
 * Shared page component for format-specific pillar indexes:
 * /tech/articles, /tech/videos, /tech/podcasts, /dance/articles, etc.
 *
 * Wrapper pages (e.g. pages/Tech/Articles.tsx) call this with their
 * specific pillar + format + hero copy.
 */
export default function PostFormatIndex({
  pillar,
  format,
  title: titleOverride,
  intro: introOverride,
  emptyMessage = "No posts yet — check back soon.",
}: PostFormatIndexProps) {
  const [activeTag, setActiveTag] = React.useState<string | null>(null);

  const copy = INDEX_COPY[`${pillar}-${format}`];
  const title = titleOverride ?? copy?.title ?? `${PILLAR_LABELS[pillar]} ${format}s`;
  const intro = introOverride ?? copy?.intro;

  const allPosts = getPostsByPillarAndFormat(pillar, format);
  const allTags = Array.from(
    new Set(allPosts.flatMap((p) => p.tags)),
  ).sort();
  const posts = activeTag
    ? allPosts.filter((p) => p.tags.includes(activeTag))
    : allPosts;

  const pillarLabel = PILLAR_LABELS[pillar];
  const siteUrl = import.meta.env.VITE_SITE_URL || "";

  return (
    <>
      <Head>
        <title>{`${title} | Ogarocious`}</title>
        <meta name="description" content={intro || `${title} from Ogarocious.`} />
        <meta property="og:title" content={`${title} | Ogarocious`} />
        <meta
          property="og:description"
          content={intro || `${title} from Ogarocious.`}
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${siteUrl}/${pillar}/${format}s`} />
        <meta
          property="og:image"
          content={import.meta.env.VITE_OG_IMAGE_URL || ""}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${title} | Ogarocious`} />
        <meta
          name="twitter:description"
          content={intro || `${title} from Ogarocious.`}
        />
        <meta
          name="twitter:image"
          content={import.meta.env.VITE_OG_IMAGE_URL || ""}
        />
      </Head>

      <PageShell variant="content">
        <Stack gap={40}>
          {/* ── Back ── */}
          <Button
            variant="subtle"
            color="gray"
            leftSection={<IconArrowLeft size={16} />}
            component={Link}
            href={`/${pillar}`}
            style={{
              color: "rgba(161, 161, 170, 0.7)",
              alignSelf: "flex-start",
            }}
          >
            Back to {pillarLabel}
          </Button>

          {/* ── Header ── */}
          <Stack gap="md" align="center">
            <Heading1 variant="shadow">{title}</Heading1>
            {intro && (
              <DimmedText
                size="lg"
                style={{
                  textAlign: "center",
                  maxWidth: "clamp(280px, 90vw, 560px)",
                }}
              >
                {intro}
              </DimmedText>
            )}
          </Stack>

          {/* ── Tag Filters ── */}
          {allTags.length > 0 && (
            <Group justify="center" gap="xs" wrap="wrap">
              <Button
                variant={activeTag === null ? "gradient" : "subtle"}
                color={activeTag === null ? undefined : "gray"}
                size="xs"
                radius="xl"
                onClick={() => setActiveTag(null)}
              >
                All
              </Button>
              {allTags.map((tag) => (
                <Badge
                  key={tag}
                  variant={activeTag === tag ? "tools" : "light"}
                  size="lg"
                  radius="xl"
                  style={{ cursor: "pointer" }}
                  onClick={() =>
                    setActiveTag(activeTag === tag ? null : tag)
                  }
                >
                  {tag}
                </Badge>
              ))}
            </Group>
          )}

          {/* ── Posts Grid ── */}
          {posts.length > 0 ? (
            <Grid>
              {posts.map((post) => (
                <Grid.Col
                  key={post.slug}
                  span={{ base: 12, sm: 6, lg: 4 }}
                >
                  <PostCard post={post} />
                </Grid.Col>
              ))}
            </Grid>
          ) : (
            <Stack align="center" py={60} gap="md">
              <DimmedText size="lg">
                {activeTag
                  ? `No posts found for "${activeTag}".`
                  : emptyMessage}
              </DimmedText>
              {activeTag && (
                <Button
                  variant="subtle"
                  color="teal"
                  onClick={() => setActiveTag(null)}
                >
                  Clear filter
                </Button>
              )}
            </Stack>
          )}
        </Stack>
      </PageShell>
    </>
  );
}
